import { useQuery } from '@tanstack/react-query';
import {
  type BankMovementResponse,
  type EmployeeListItem,
  formatMoney,
  fromCents,
  nameKey,
  toCents,
} from '@k-management/shared';
import { useEffect } from 'react';
import { DecimalInput } from '../../components/DecimalInput.js';
import { Icon } from '../../components/Icon.js';
import { apiRequest } from '../../lib/api.js';
import { moneyOf } from '../people/people-ui.js';

export interface TfrLine {
  employeeId: string;
  /** As typed. */
  amount: string;
}

/** The cents of the lines, null when an amount cannot be read. */
export function tfrLinesCents(lines: readonly TfrLine[]): number | null {
  let total = 0;
  for (const line of lines) {
    const amount = moneyOf(line.amount);
    if (amount === null) return null;
    total += toCents(amount);
  }
  return total;
}

/** Whether the bank names the fund of the employee: every word of it in the description. */
function citesFund(employee: EmployeeListItem, text: string): boolean {
  if (employee.tfr !== 'fondo' || !employee.pensionFundName) return false;
  const words = nameKey(employee.pensionFundName)
    .toUpperCase()
    .split(/[^A-Z0-9]+/)
    .filter((word) => word.length >= 3 && !['FONDO', 'PENSIONE', 'APERTO'].includes(word));
  return words.length > 0 && words.every((word) => text.includes(word));
}

/**
 * The TFR a transfer pays to the pension funds (13/10/2026, 1.68): one line
 * per employee with the amount. The employees of the fund the bank names come
 * first and are proposed; the period covered is given later, in the card of
 * the employee. For who has the module Persone.
 */
export function TfrLines({
  movement,
  lines,
  onChange,
}: {
  movement: BankMovementResponse;
  lines: TfrLine[];
  onChange: (lines: TfrLine[]) => void;
}) {
  const employees = useQuery({
    queryKey: ['people', 'employees', 'dipendenti'],
    queryFn: () =>
      apiRequest<EmployeeListItem[]>('/people/employees').then((items) =>
        // Collaborators are paid on their invoices (2.38).
        items.filter((item) => item.relationship === 'dipendente'),
      ),
  });
  const text = nameKey(`${movement.description} ${movement.counterparty ?? ''}`).toUpperCase();
  const bookingYear = Number(movement.bookingDate.slice(0, 4));
  const movementCents = Math.abs(toCents(movement.amount));
  // In force in the year before or after, those with a fund first, the fund the bank names before all.
  const rank = (item: EmployeeListItem) =>
    citesFund(item, text) ? 0 : item.tfr === 'fondo' ? 1 : 2;
  const people = (employees.data ?? [])
    .filter((item) => item.leftOn === null || item.leftOn >= `${bookingYear - 1}-01-01`)
    .sort((a, b) => rank(a) - rank(b));
  const cited = people.filter((item) => citesFund(item, text));

  // The employees arrive: those of the fund named are the first lines.
  useEffect(() => {
    if (!employees.data || lines.length || !cited.length) return;
    onChange(
      cited.map((item) => ({
        employeeId: item.id,
        amount: cited.length === 1 ? fromCents(movementCents) : '',
      })),
    );
    // Only when the employees arrive.
  }, [employees.data]);

  const total = tfrLinesCents(lines);
  const set = (index: number, change: Partial<TfrLine>) =>
    onChange(lines.map((line, at) => (at === index ? { ...line, ...change } : line)));

  return (
    <div className="stack salary-lines">
      {employees.isPending && <p className="muted small">Ricerca dei dipendenti…</p>}
      {cited.length > 0 && (
        <p className="muted small">
          {cited.length === 1 ? 'Il dipendente' : 'I dipendenti'} del fondo citato nella
          descrizione.
        </p>
      )}
      {employees.data && lines.length === 0 && (
        <p className="muted small">
          Aggiungi i dipendenti a cui va il versamento, o conferma solo il motivo.
        </p>
      )}
      {lines.map((line, index) => (
        <div key={index} className="salary-line">
          <select
            aria-label="Dipendente"
            value={line.employeeId}
            onChange={(event) => set(index, { employeeId: event.target.value })}
          >
            {people.map((item) => (
              <option key={item.id} value={item.id}>
                {item.lastName} {item.firstName}
                {item.tfr === 'fondo' && item.pensionFundName
                  ? ` · ${item.pensionFundName}`
                  : ' · TFR in azienda'}
              </option>
            ))}
          </select>
          <DecimalInput
            aria-label="Importo"
            value={line.amount}
            onChange={(value) => set(index, { amount: value })}
            decimals={[2, 2]}
          />
          <button
            type="button"
            className="icon-button"
            title="Togli la riga"
            aria-label="Togli la riga"
            onClick={() => onChange(lines.filter((_line, at) => at !== index))}
          >
            <Icon name="close" />
          </button>
        </div>
      ))}
      {people.length > lines.length && (
        <button
          type="button"
          className="link"
          onClick={() =>
            onChange([
              ...lines,
              {
                employeeId: people.find(
                  (item) => !lines.some((line) => line.employeeId === item.id),
                )!.id,
                amount: total === null ? '' : fromCents(Math.max(0, movementCents - total)),
              },
            ])
          }
        >
          + aggiungi un dipendente
        </button>
      )}
      {employees.data && people.length === 0 && (
        <p className="muted small">Nessun dipendente in Organizzazione › Persone.</p>
      )}
      {lines.length > 0 && (
        <div className="reconcile-sum">
          <table>
            <tbody>
              <tr>
                <td>Assegnato</td>
                <td>{total === null ? '—' : formatMoney(fromCents(total))}</td>
              </tr>
              <tr>
                <td>Movimento</td>
                <td>{formatMoney(fromCents(movementCents))}</td>
              </tr>
              <tr className="total">
                <td>
                  Differenza
                  {total !== null &&
                    total !== movementCents &&
                    (total < movementCents ? ': resta da assegnare' : ': supera il movimento')}
                </td>
                <td className={total === movementCents ? 'ok' : 'warn'}>
                  {total === null ? '—' : formatMoney(fromCents(movementCents - total))}
                  {total === movementCents && ' ✓'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <p className="muted small">
        Il periodo coperto da ogni versamento si indica poi nella scheda del dipendente, sezione
        «TFR».
      </p>
    </div>
  );
}
